import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../hooks/useAuth";
import { 
  User, Camera, Globe, Link as LinkIcon, Twitter, 
  Save, Loader2, Lock, Eye, EyeOff, MapPin, Upload, Mail, CheckCircle, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, refreshUser, resendVerification, isVerified } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const fileInputRef = useRef(null);
  
  // Profile form state
  const [profile, setProfile] = useState({
    name: "",
    bio: "",
    website: "",
    location: "",
    picture_url: "",
    skills: [],
    social_links: {
      twitter: "",
      instagram: "",
      linkedin: "",
      youtube: "",
      vimeo: ""
    }
  });
  const [skillInput, setSkillInput] = useState("");
  
  // Password form state
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/me`, { withCredentials: true });
      if (response.data.profile) {
        setProfile({
          name: response.data.name || user?.name || "",
          bio: response.data.profile.bio || "",
          website: response.data.profile.website || "",
          location: response.data.profile.location || "",
          picture_url: response.data.profile.picture_url || response.data.picture || "",
          skills: response.data.profile.skills || [],
          social_links: {
            twitter: response.data.profile.social_links?.twitter || "",
            instagram: response.data.profile.social_links?.instagram || "",
            linkedin: response.data.profile.social_links?.linkedin || "",
            youtube: response.data.profile.social_links?.youtube || "",
            vimeo: response.data.profile.social_links?.vimeo || ""
          }
        });
      } else {
        // No profile yet, just set the name and picture
        setProfile(prev => ({
          ...prev,
          name: response.data.name || user?.name || "",
          picture_url: response.data.picture || ""
        }));
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile. Please try refreshing the page.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/me`, profile, { withCredentials: true });
      toast.success("Profile updated successfully!");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/profile/picture`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProfile(prev => ({ ...prev, picture_url: response.data.picture_url }));
      toast.success("Profile picture updated!");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload picture");
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleAddSkill = (e) => {
    e.preventDefault();
    if (skillInput.trim() && !profile.skills.includes(skillInput.trim())) {
      setProfile({
        ...profile,
        skills: [...profile.skills, skillInput.trim()]
      });
      setSkillInput("");
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setProfile({
      ...profile,
      skills: profile.skills.filter(skill => skill !== skillToRemove)
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwords.new !== passwords.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (passwords.new.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwords.current,
        new_password: passwords.new
      }, { withCredentials: true });
      
      toast.success("Password changed successfully!");
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="My Profile" 
        description="Manage your AI Film Society profile settings"
        noIndex={true}
      />
      
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-syne text-3xl md:text-4xl font-bold text-white mb-2">
            My Profile
          </h1>
          <p className="text-slate-400">
            Manage your profile information and account settings
          </p>
        </motion.div>

        {/* Email Verification Banner */}
        {!isVerified && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="p-4 bg-amber-500/10 border border-amber-500/30 mb-8 flex items-start gap-4"
          >
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-grow">
              <h3 className="text-amber-400 font-medium mb-1">Verify Your Email</h3>
              <p className="text-sm text-slate-400 mb-3">
                Please verify your email to access community features like posting in the forum, 
                submitting films, and applying for jobs.
              </p>
              <Button
                onClick={async () => {
                  setSendingVerification(true);
                  try {
                    await resendVerification();
                    toast.success("Verification email sent! Check your inbox.");
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to send verification email");
                  } finally {
                    setSendingVerification(false);
                  }
                }}
                disabled={sendingVerification}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                {sendingVerification ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Resend Verification Email
              </Button>
            </div>
          </motion.div>
        )}

        {isVerified && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="p-3 bg-green-500/10 border border-green-500/30 mb-8 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-sm text-green-400">Email verified - Full community access enabled</span>
          </motion.div>
        )}

        {/* User Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 bg-void-surface border border-white/5 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="relative group">
              <div className="w-20 h-20 rounded-full bg-purple/20 flex items-center justify-center overflow-hidden">
                {(profile.picture_url || user?.picture) ? (
                  <img src={profile.picture_url || user?.picture} alt={user?.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-purple-light" />
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
                className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                {uploadingPicture ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePictureUpload}
                className="hidden"
              />
            </div>
            <div>
              <h2 className="font-syne font-bold text-white text-xl">{user?.name}</h2>
              <p className="text-slate-400">{user?.email}</p>
              {user?.role && user.role !== "user" && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-purple/20 text-purple-light">
                  {user.role.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10">
          <button
            onClick={() => setActiveTab("profile")}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === "profile" 
                ? "text-purple-light border-b-2 border-purple" 
                : "text-slate-400 hover:text-white"
            }`}
          >
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab("security")}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === "security" 
                ? "text-purple-light border-b-2 border-purple" 
                : "text-slate-400 hover:text-white"
            }`}
          >
            Security
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Display Name */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Display Name
              </label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Your name"
                className="bg-void border-white/10 text-white"
                data-testid="profile-name"
              />
              <p className="text-xs text-slate-500 mt-1">This is how you'll appear to other members</p>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">Bio</label>
              <Textarea
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                placeholder="Tell the community about yourself..."
                className="bg-void border-white/10 text-white min-h-[120px]"
                data-testid="profile-bio"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location
              </label>
              <Input
                value={profile.location}
                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                placeholder="e.g., Los Angeles, CA"
                className="bg-void border-white/10 text-white"
                data-testid="profile-location"
              />
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">
                <Globe className="w-4 h-4 inline mr-1" />
                Website
              </label>
              <Input
                value={profile.website}
                onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                placeholder="https://yourwebsite.com"
                className="bg-void border-white/10 text-white"
                data-testid="profile-website"
              />
            </div>

            {/* Skills */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">Skills & Expertise</label>
              <form onSubmit={handleAddSkill} className="flex gap-2 mb-3">
                <Input
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  placeholder="Add a skill (e.g., AI Animation, Directing)"
                  className="bg-void border-white/10 text-white flex-grow"
                  data-testid="profile-skill-input"
                />
                <Button type="submit" variant="outline" className="border-white/20 text-white">
                  Add
                </Button>
              </form>
              {profile.skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, i) => (
                    <span 
                      key={i}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-purple/20 text-purple-light text-sm"
                    >
                      {skill}
                      <button 
                        onClick={() => handleRemoveSkill(skill)}
                        className="ml-1 text-purple-light/50 hover:text-white"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Social Links */}
            <div>
              <label className="block text-sm text-slate-400 mb-4">Social Links</label>
              <p className="text-xs text-slate-500 mb-4">Just enter your @username - no need for full URLs!</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Twitter/X</label>
                  <Input
                    value={profile.social_links.twitter}
                    onChange={(e) => setProfile({ 
                      ...profile, 
                      social_links: { ...profile.social_links, twitter: e.target.value }
                    })}
                    placeholder="@username"
                    className="bg-void border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Instagram</label>
                  <Input
                    value={profile.social_links.instagram}
                    onChange={(e) => setProfile({ 
                      ...profile, 
                      social_links: { ...profile.social_links, instagram: e.target.value }
                    })}
                    placeholder="@username"
                    className="bg-void border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">LinkedIn</label>
                  <Input
                    value={profile.social_links.linkedin}
                    onChange={(e) => setProfile({ 
                      ...profile, 
                      social_links: { ...profile.social_links, linkedin: e.target.value }
                    })}
                    placeholder="@username or profile URL"
                    className="bg-void border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">YouTube</label>
                  <Input
                    value={profile.social_links.youtube}
                    onChange={(e) => setProfile({ 
                      ...profile, 
                      social_links: { ...profile.social_links, youtube: e.target.value }
                    })}
                    placeholder="@channelname"
                    className="bg-void border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Vimeo</label>
                  <Input
                    value={profile.social_links.vimeo}
                    onChange={(e) => setProfile({ 
                      ...profile, 
                      social_links: { ...profile.social_links, vimeo: e.target.value }
                    })}
                    placeholder="@username"
                    className="bg-void border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="bg-purple hover:bg-purple-hover text-white"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === "security" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="p-6 bg-void-surface border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <Lock className="w-5 h-5 text-purple-light" />
                <h3 className="font-syne font-bold text-white text-lg">Change Password</h3>
              </div>
              
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Current Password</label>
                  <div className="relative">
                    <Input
                      type={showPasswords.current ? "text" : "password"}
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      placeholder="Enter current password"
                      className="bg-void border-white/10 text-white pr-10"
                      data-testid="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">New Password</label>
                  <div className="relative">
                    <Input
                      type={showPasswords.new ? "text" : "password"}
                      value={passwords.new}
                      onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                      placeholder="Enter new password (min 6 characters)"
                      className="bg-void border-white/10 text-white pr-10"
                      data-testid="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Confirm New Password</label>
                  <div className="relative">
                    <Input
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      placeholder="Confirm new password"
                      className="bg-void border-white/10 text-white pr-10"
                      data-testid="confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={changingPassword || !passwords.current || !passwords.new || !passwords.confirm}
                  className="bg-purple hover:bg-purple-hover text-white"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    "Change Password"
                  )}
                </Button>
              </form>

              <p className="text-xs text-slate-500 mt-4">
                Note: If you signed up with Google, you cannot change your password here.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
